import { useEffect, useState } from 'react';
import type { ListaMisPagos, MiPago, MiPerfil, OperacionesClienteFinal } from '../api/ops/clienteFinal';
import type { ConceptoPago, EstadoPago } from '../api/ops/pagos';
import { ErrorApi } from '../api/problema';
import { Chip, type EstadoChip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { dinero } from '../logica/dinero';
import { navegador } from '../logica/navegador';
import { haySiguiente, usePaginacionCursor } from '../logica/paginacion';
import { BotonPc, Pagina } from './Pagina';

/**
 * «Mis pagos» (4.10, `/mis-pagos`): con el token de cliente de «Mis datos» (2.9), el negocio a elegir entre los perfiles
 * de su Cuenta Reservaria (`listMyBusinessProfiles`, G35) y sus pagos ahí (`listMyPayments`): qué, de qué e importe; chip
 * solo si se devolvió, en parte o del todo, o si se liberó la garantía; el detalle con las devoluciones una a una (G293),
 * y «Ver el recibo» de Stripe (G96). «Anterior / Siguiente» (G121). Pie con las dos privacidades (G195).
 */
const CONCEPTO: Readonly<Record<ConceptoPago, readonly [string, NombreIcono]>> = {
    sale: ['Compra', 'ticket'],
    deposit: ['Señal', 'calendar-check'],
    guarantee: ['Tarjeta de garantía', 'credit-card'],
    no_show_fee: ['Cargo por no presentarte', 'user-x'],
    late_cancellation_fee: ['Cargo por cancelar tarde', 'calendar-x'],
    balance: ['Pagado en el local', 'banknote'],
};

const CHIP: Partial<Record<EstadoPago, readonly [string, EstadoChip]>> = {
    partially_refunded: ['Devuelto en parte', 'confirmed'],
    refunded: ['Devuelto', 'confirmed'],
    released: ['Liberada', 'neutral'],
    lost_in_dispute: ['Devuelto por tu banco', 'confirmed'],
    disputed: ['En disputa', 'problem'],
    processing: ['En proceso', 'pending'],
    expired: ['Caducado', 'problem'],
    rejected: ['Rechazado', 'problem'],
    voided: ['Anulado', 'problem'],
};

const NOTA: Partial<Record<EstadoPago, readonly [NombreIcono, string]>> = {
    pending: ['clock', 'Esperando el pago. Si no se paga en 30 minutos, caduca y no se cobra nada.'],
    processing: ['clock', 'Tu banco todavía no ha confirmado el pago. Te avisamos en cuanto lo haga; no vuelvas a pagar.'],
    guarantee_saved: ['shield-check', 'No te hemos cobrado nada. Tu tarjeta está guardada en Stripe por si no vas o cancelas tarde.'],
    released: ['circle-check', 'No se cobró nada: tu tarjeta de garantía ya no está retenida.'],
    partially_refunded: ['undo-2', 'Te hemos devuelto una parte. Puede tardar unos días en verse en tu cuenta.'],
    refunded: ['undo-2', 'Te lo hemos devuelto entero. Puede tardar unos días en verse en tu cuenta.'],
    disputed: ['scale', 'Abriste una reclamación con tu banco por este pago. La resuelve tu banco; si tienes dudas, habla con el negocio.'],
    lost_in_dispute: ['undo-2', 'Tu banco te dio la razón y te devolvió el dinero.'],
    expired: ['circle-check', 'No se pagó a tiempo y no se cobró nada.'],
    rejected: ['circle-check', 'El pago no se hizo y no se cobró nada.'],
    registered: ['store', 'Lo pagaste en el local. El justificante te lo da el negocio.'],
    voided: ['store', 'El negocio anuló este registro. No es un cobro.'],
};

const TACHADO: ReadonlySet<EstadoPago> = new Set(['refunded', 'released', 'expired', 'rejected', 'voided', 'lost_in_dispute']);

const fecha = (iso: string) => {
    const f = new Date(iso);
    return Number.isNaN(f.getTime()) ? '' : f.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
};

interface Props {
    ops: OperacionesClienteFinal;
    onSalir: () => void;
    onCaducada: () => void;
}

export function MisPagos({ ops, onSalir, onCaducada }: Props) {
    const [perfiles, setPerfiles] = useState<MiPerfil[] | null>(null);
    const [error, setError] = useState(false);
    const [elegido, setElegido] = useState<string | null>(null);
    const [lista, setLista] = useState<{ clave: string; datos: ListaMisPagos | null; error: boolean } | null>(null);
    const [abierto, setAbierto] = useState<string | null>(null);
    const [intento, setIntento] = useState(0);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;

    useEffect(() => {
        let vivo = true;
        ops.listMyBusinessProfiles({ limit: 100 })
            .then((l) => {
                if (vivo) {
                    setPerfiles(l.data.filter((p) => p.ended == null));
                    setError(false);
                }
            })
            .catch((e: unknown) => {
                if (!vivo) {
                    return;
                }
                if (e instanceof ErrorApi && e.estado === 401) {
                    onCaducada();
                } else {
                    setError(true);
                }
            });
        return () => {
            vivo = false;
        };
        // onCaducada cambia en cada pintado: solo se mira al cargar.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, intento]);

    const perfil = perfiles?.find((p) => p.customer_id === elegido) ?? perfiles?.[0] ?? null;
    const idPerfil = perfil?.customer_id ?? null;
    const clave = `${idPerfil ?? ''}|${pag.cursor ?? ''}|${String(intento)}`;
    useEffect(() => {
        if (idPerfil === null) {
            return;
        }
        let vivo = true;
        ops.listMyPayments(idPerfil, { cursor: pag.cursor, limit: 20 })
            .then((datos) => {
                if (vivo) {
                    setLista({ clave, datos, error: false });
                }
            })
            .catch((e: unknown) => {
                if (!vivo) {
                    return;
                }
                if (e instanceof ErrorApi && e.estado === 401) {
                    onCaducada();
                } else {
                    setLista({ clave, datos: null, error: true });
                }
            });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, idPerfil, pag.cursor, clave]);

    const nombre = perfil?.business.name ?? '';
    const cabecera = perfil === null ? ({ tipo: 'cuenta' } as const) : ({ tipo: 'negocio', nombre, linea: 'Mis pagos' } as const);
    const pie = perfil === null ? ({ tipo: 'cuenta' } as const) : ({ tipo: 'negocio', nombre, privacidad: perfil.business.privacy_url ?? null } as const);
    const reintentar = () => { setIntento((i) => i + 1); };

    if (error) {
        return (
            <Pagina cabecera={{ tipo: 'cuenta' }} pie={{ tipo: 'cuenta' }} onSalir={onSalir} centrada>
                <section className="rv-pc-card">
                    <div className="rv-pc-card__cab"><h1>No hemos podido cargar tus pagos</h1><span>Comprueba la conexión y prueba de nuevo.</span></div>
                    <BotonPc texto="Reintentar" icono="refresh-cw" onClick={reintentar} />
                </section>
            </Pagina>
        );
    }

    const actual = lista?.clave === clave ? lista : null;
    const pagos = actual?.datos?.data ?? null;

    return (
        <Pagina
            cabecera={cabecera}
            pie={pie}
            onSalir={onSalir}
            titulo={{ titulo: 'Mis pagos', sub: nombre === '' ? 'Lo que has pagado, lo que te hemos devuelto y tus recibos.' : `Lo que has pagado en ${nombre}, lo que te hemos devuelto y tus recibos.` }}
        >
            {perfiles !== null && perfiles.length > 1 && (
                <label className="rv-pc-negocio__canal">
                    <span>Negocio</span>
                    <select
                        className="rv-pc-select rv-pc-select--ancho"
                        value={perfil?.customer_id ?? ''}
                        onChange={(e) => {
                            setElegido(e.target.value);
                            setAbierto(null);
                            reiniciar();
                        }}
                    >
                        {perfiles.map((p) => (
                            <option key={p.customer_id} value={p.customer_id}>{p.business.name}</option>
                        ))}
                    </select>
                </label>
            )}
            <section className="rv-pc-card rv-pc-card--seccion" aria-label="Tus pagos" aria-busy={perfiles === null || (perfil !== null && actual === null)}>
                {perfiles === null || (perfil !== null && actual === null) ? (
                    <>
                        <div className="rv-pc-esqueleto" style={{ height: 56 }} />
                        <div className="rv-pc-esqueleto" style={{ height: 56, marginTop: 12 }} />
                    </>
                ) : actual?.error === true ? (
                    <div className="rv-pc-vacio">
                        <span><Icono nombre="circle-alert" /></span>
                        <strong>No hemos podido cargar tus pagos</strong>
                        <BotonPc texto="Reintentar" icono="refresh-cw" tipo="sec" peq onClick={reintentar} />
                    </div>
                ) : pagos === null || pagos.length === 0 ? (
                    <div className="rv-pc-vacio">
                        <span><Icono nombre="receipt" /></span>
                        <strong>Todavía no tienes pagos aquí</strong>
                        <small>Cuando pagues una señal, una entrada o guardes tu tarjeta, lo verás aquí con su recibo.</small>
                    </div>
                ) : (
                    pagos.map((p) => <FilaPago key={p.id} pago={p} abierto={abierto === p.id} onAbrir={() => { setAbierto((a) => (a === p.id ? null : p.id)); }} />)
                )}
            </section>
            {actual?.datos != null && (pag.hayAnterior || haySiguiente(actual.datos.next_cursor)) && (
                <div className="rv-pc-paginas">
                    <BotonPc texto="Anterior" icono="arrow-left" tipo="sec" peq desactivado={!pag.hayAnterior} onClick={pag.anterior} />
                    <BotonPc texto="Siguiente" icono="arrow-right" tipo="sec" peq desactivado={!haySiguiente(actual.datos.next_cursor)} onClick={() => { pag.siguiente(actual.datos?.next_cursor ?? null); }} />
                </div>
            )}
            <p className="rv-pc-nota rv-final-osc"><Icono nombre="lock" /><span>Los pagos con tarjeta los procesa Stripe. Nunca vemos ni guardamos los datos de tu tarjeta.</span></p>
        </Pagina>
    );
}

function FilaPago({ pago, abierto, onAbrir }: { pago: MiPago; abierto: boolean; onAbrir: () => void }) {
    const [titulo, icono] = CONCEPTO[pago.concept];
    const chip = CHIP[pago.status];
    const nota = NOTA[pago.status];
    const id = `rv-pc-pago-${pago.id}`;
    return (
        <div className="rv-pc-pago">
            <button type="button" className="rv-pc-pago__cab" aria-expanded={abierto} aria-controls={id} onClick={onAbrir}>
                <span className="rv-pc-pago__icono" aria-hidden="true"><Icono nombre={icono} /></span>
                <span>
                    <strong>{titulo}</strong>
                    {pago.description !== undefined && <small>{pago.description}</small>}
                </span>
                <span className="rv-pc-pago__importe">
                    {TACHADO.has(pago.status) ? <s>{dinero(pago.amount)}</s> : <strong>{dinero(pago.amount)}</strong>}
                    {chip !== undefined && <Chip estado={chip[1]}>{chip[0]}</Chip>}
                </span>
                <Icono nombre="chevron-down" />
            </button>
            {abierto && (
                <div id={id} className="rv-pc-pago__cuerpo">
                    <div className="rv-pc-dato"><span>Fecha</span><strong>{fecha(pago.created_at)}</strong></div>
                    <div className="rv-pc-dato"><span>Importe</span><strong>{dinero(pago.amount)}</strong></div>
                    {pago.booking_fee !== undefined && pago.booking_fee.amount > 0 && <div className="rv-pc-dato"><span>Gastos de gestión (incluidos)</span><strong>{dinero(pago.booking_fee)}</strong></div>}
                    {(pago.refunds ?? []).map((r, i) => (
                        <div key={`${r.completed_at}${String(i)}`} className="rv-pc-dato"><span>{`Devuelto el ${fecha(r.completed_at)}`}</span><strong>{dinero(r.amount)}</strong></div>
                    ))}
                    {nota !== undefined && <p className="rv-pc-nota"><Icono nombre={nota[0]} /><span>{nota[1]}</span></p>}
                    {pago.receipt_url != null && (
                        <BotonPc texto="Ver el recibo" icono="receipt" tipo="sec" peq onClick={() => { navegador.abrir(pago.receipt_url ?? ''); }} />
                    )}
                </div>
            )}
        </div>
    );
}
